import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Shield,
  Zap,
} from 'lucide-react';
import { getApiUrl } from '../utils/api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        getApiUrl('/api/auth/forgot-password'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: normalizedEmail,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.message || 'Une erreur est survenue.'
        );
      }

      setSuccess(true);
    } catch (err) {
      console.error('Forgot password error:', err);

      setError(
        err instanceof Error
          ? err.message
          : 'Une erreur est survenue. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-background to-accent-50 flex">

      {/* Partie gauche */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-300 via-accent-500 to-secondary-400 relative overflow-hidden">

        <div className="absolute inset-0 bg-black/10" />

        <div className="relative z-10 flex flex-col justify-center px-12 text-white">

          <div className="mb-8">

            <div className="flex items-center space-x-4 mb-6">

              <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl">
                <Zap className="w-8 h-8 text-white" />
              </div>

              <div>
                <h1 className="text-3xl font-display font-bold">
                  Digital Tattoo
                </h1>

                <p className="text-white/90">
                  Studio Management System
                </p>
              </div>
            </div>

            <h2 className="text-4xl font-display font-bold mb-4 leading-tight">
              Réinitialisez votre mot de passe
            </h2>

            <p className="text-xl text-white/90 leading-relaxed">
              Saisissez l'adresse email associée à votre compte.
              Nous vous enverrons les instructions permettant de
              définir un nouveau mot de passe.
            </p>
          </div>

          <div className="flex items-start space-x-4">

            <div className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl">
              <Shield className="w-6 h-6 text-white" />
            </div>

            <div>
              <h3 className="font-semibold text-lg">
                Réinitialisation sécurisée
              </h3>

              <p className="text-white/80">
                Le lien de réinitialisation est personnel et limité
                dans le temps.
              </p>
            </div>
          </div>
        </div>

        <div className="absolute top-1/4 right-10 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute bottom-1/4 left-10 w-24 h-24 bg-white/10 rounded-full blur-xl" />

      </div>

      {/* Partie droite */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">

        <div className="max-w-md w-full space-y-8">

          {/* Header mobile */}
          <div className="text-center lg:hidden">

            <div className="flex items-center justify-center space-x-3 mb-4">

              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-300 to-accent-500 rounded-xl">
                <Zap className="w-6 h-6 text-white" />
              </div>

              <h1 className="text-2xl font-display font-bold text-textPrimary">
                Digital Tattoo
              </h1>

            </div>

          </div>

          {!success ? (
            <>
              <div>
                <h2 className="text-3xl font-display font-bold text-textPrimary text-center">
                  Mot de passe oublié ?
                </h2>

                <p className="mt-2 text-center text-textSecondary">
                  Entrez votre adresse email pour recevoir un lien
                  de réinitialisation.
                </p>
              </div>

              <form
                className="mt-8 space-y-6"
                onSubmit={handleSubmit}
              >

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 text-sm">
                      {error}
                    </p>
                  </div>
                )}

                <fieldset
                  disabled={loading}
                  className={loading ? 'opacity-60' : ''}
                >

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-textPrimary mb-2"
                    >
                      Adresse email
                    </label>

                    <div className="relative">

                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>

                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) =>
                          setEmail(e.target.value)
                        }
                        className="input-field pl-10"
                        placeholder="votre@email.com"
                      />

                    </div>
                  </div>

                </fieldset>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />

                      Envoi en cours...
                    </div>
                  ) : (
                    'Envoyer le lien de réinitialisation'
                  )}
                </button>

              </form>

              <div className="text-center">

                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour à la connexion
                </Link>

              </div>
            </>
          ) : (
            <div className="text-center">

              <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <Mail className="w-8 h-8 text-green-600" />
              </div>

              <h2 className="text-3xl font-display font-bold text-textPrimary">
                Consultez votre boîte mail
              </h2>

              <p className="mt-4 text-textSecondary leading-relaxed">
                Si un compte correspond à l'adresse
                <span className="font-medium text-textPrimary">
                  {' '}
                  {email}
                </span>
                , vous recevrez un email contenant les instructions
                permettant de réinitialiser votre mot de passe.
              </p>

              <div className="mt-8">

                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour à la connexion
                </Link>

              </div>

            </div>
          )}

        </div>

      </div>

    </div>
  );
}

export default ForgotPasswordPage;